import type {
  Employee,
  Role,
  Location,
  Position,
  PayrollDepartment,
  Schedule,
  PaymentType,
  PaymentFrequency,
  PayRecord,
  EmployeeLicense,
  FeedbackRecord,
  TrainingRecord,
  EmployeeNote,
  LeavePolicy,
} from '../types';

export interface FilterCondition {
  id: string;
  field: string;
  value: string | number | boolean | number[] | string[];
  valueFrom?: string | number | null;
  valueTo?: string | number | null;
}

export type FilterFieldType =
  | 'text'
  | 'date'
  | 'select'
  | 'boolean'
  | 'number'
  | 'dateRange'
  | 'numberRange'
  | 'multiSelect';

export interface FilterFieldOption {
  value: string;
  label: string;
  type: FilterFieldType;
  tab: string;
  options?: Array<{ value: string | number; label: string }>;
}

export function getFieldTypeFromList(
  filterFields: FilterFieldOption[],
  fieldValue: string
): FilterFieldType {
  const field = filterFields.find((f) => f.value === fieldValue);
  return field?.type || 'text';
}

/** Human-readable value for one filter row (used in report history, etc.). */
export function summarizeFilterConditionValue(
  filter: FilterCondition,
  fieldType: FilterFieldType,
  fieldOptions: Array<{ value: string | number; label: string }>
): string {
  if (fieldType === 'dateRange' || fieldType === 'numberRange') {
    const from = filter.valueFrom != null && filter.valueFrom !== '' ? String(filter.valueFrom) : '—';
    const to = filter.valueTo != null && filter.valueTo !== '' ? String(filter.valueTo) : '—';
    return `${from} → ${to}`;
  }
  if (fieldType === 'boolean') {
    return filter.value === true ? 'Yes' : filter.value === false ? 'No' : '—';
  }
  if (fieldType === 'multiSelect' && Array.isArray(filter.value)) {
    const ids = filter.value as (string | number)[];
    const labels = ids.map((id) => {
      const opt = fieldOptions.find((o) => String(o.value) === String(id));
      return opt?.label ?? String(id);
    });
    return labels.length ? labels.join(', ') : '—';
  }
  if (fieldType === 'select' && fieldOptions.length > 0) {
    const opt = fieldOptions.find((o) => String(o.value) === String(filter.value));
    if (opt) return opt.label;
    if (filter.value === null || filter.value === undefined || filter.value === '') {
      const emptyOpt = fieldOptions.find((o) => o.value === '' || o.value === null || o.value === undefined);
      return emptyOpt?.label ?? '—';
    }
    return String(filter.value);
  }
  return filter.value !== '' && filter.value != null ? String(filter.value) : '—';
}

export function buildEmployeeReportFilterSummaries(
  filters: FilterCondition[],
  filterFields: FilterFieldOption[]
): Array<{ fieldKey: string; fieldLabel: string; valueText: string }> {
  return filters
    .filter((f) => !!f.field)
    .map((f) => {
      const field = filterFields.find((x) => x.value === f.field);
      const fieldType = getFieldTypeFromList(filterFields, f.field);
      const options = field?.options || [];
      return {
        fieldKey: f.field,
        fieldLabel: field?.label ?? f.field,
        valueText: summarizeFilterConditionValue(f, fieldType, options),
      };
    });
}

export interface BuildEmployeeFilterFieldsParams {
  locations: Location[];
  positions: Position[];
  payrollDepartments: PayrollDepartment[];
  schedules: Schedule[];
  roles: Role[];
  paymentTypes: PaymentType[];
  paymentFrequencies: PaymentFrequency[];
  leavePolicies: LeavePolicy[];
}

/** Status for employee list reports (Active / Inactive / All). Stored as a normal filter row; applied via statusFilter param in filterEmployeesWithAdvancedFilters. */
export function getStatusFilterFromFilters(filters: FilterCondition[]): 'Active' | 'InActive' | 'All' {
  const row = filters.find((f) => f.field === 'employeeReportStatus');
  if (!row || row.value === '' || row.value == null) return 'Active';
  const v = String(row.value);
  if (v === 'InActive' || v === 'Inactive') return 'InActive';
  if (v === 'All') return 'All';
  return 'Active';
}

export function buildEmployeeFilterFields(p: BuildEmployeeFilterFieldsParams): FilterFieldOption[] {
  const {
    locations,
    positions,
    payrollDepartments,
    schedules,
    roles,
    paymentTypes,
    paymentFrequencies,
    leavePolicies,
  } = p;
  return [
    {
      value: 'employeeReportStatus',
      label: 'Status',
      type: 'select',
      tab: 'Report',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'InActive', label: 'Inactive' },
        { value: 'All', label: 'All' },
      ],
    },
    { value: 'email', label: 'Email', type: 'text', tab: 'Login' },
    { value: 'pin', label: 'PIN', type: 'text', tab: 'Login' },
    {
      value: 'title',
      label: 'Title',
      type: 'select',
      tab: 'Personal',
      options: [
        { value: '', label: 'Any' },
        { value: 'Mr', label: 'Mr' },
        { value: 'Mrs', label: 'Mrs' },
        { value: 'Ms', label: 'Ms' },
        { value: 'Miss', label: 'Miss' },
        { value: 'Mx', label: 'Mx' },
        { value: 'Dr', label: 'Dr' },
        { value: 'Prof', label: 'Prof' },
      ],
    },
    { value: 'firstName', label: 'First Name', type: 'text', tab: 'Personal' },
    { value: 'lastName', label: 'Last Name', type: 'text', tab: 'Personal' },
    {
      value: 'gender',
      label: 'Gender',
      type: 'select',
      tab: 'Personal',
      options: [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' },
        { value: 'Prefer Not to Say', label: 'Prefer Not to Say' },
      ],
    },
    { value: 'genderOther', label: 'Gender (Other)', type: 'text', tab: 'Personal' },
    { value: 'birthday', label: 'Birthday', type: 'dateRange', tab: 'Personal' },
    { value: 'phone', label: 'Phone', type: 'text', tab: 'Personal' },
    { value: 'address', label: 'Address (legacy)', type: 'text', tab: 'Personal' },
    { value: 'address1', label: 'Address line 1', type: 'text', tab: 'Personal' },
    { value: 'address2', label: 'Address line 2', type: 'text', tab: 'Personal' },
    { value: 'address3', label: 'Address line 3', type: 'text', tab: 'Personal' },
    { value: 'county', label: 'County', type: 'text', tab: 'Personal' },
    { value: 'zipCode', label: 'Zip code', type: 'text', tab: 'Personal' },
    { value: 'psaLicense', label: 'PSA License', type: 'text', tab: 'Personal' },
    { value: 'startDate', label: 'Start Date', type: 'dateRange', tab: 'Personal' },
    { value: 'visaNumber', label: 'Visa Number', type: 'text', tab: 'Personal' },
    { value: 'visaExpiryDate', label: 'Visa Expiry Date', type: 'dateRange', tab: 'Personal' },
    { value: 'comment', label: 'Comment', type: 'text', tab: 'Personal' },
    {
      value: 'mainLocationId',
      label: 'Main Location',
      type: 'select',
      tab: 'Work',
      options: [
        { value: '', label: 'All locations' },
        ...locations.map((loc) => ({ value: loc.id, label: loc.name })),
      ],
    },
    {
      value: 'mainOrSubLocationIds',
      label: 'Main or Sub Locations',
      type: 'multiSelect',
      tab: 'Work',
      options: locations.map((loc) => ({ value: loc.id, label: loc.name })),
    },
    {
      value: 'mainPositionId',
      label: 'Main Position',
      type: 'select',
      tab: 'Work',
      options: positions.map((pos) => ({ value: pos.id, label: pos.title })),
    },
    {
      value: 'mainOrSubPositionIds',
      label: 'Main or Sub Positions',
      type: 'multiSelect',
      tab: 'Work',
      options: positions.map((pos) => ({ value: pos.id, label: pos.title })),
    },
    {
      value: 'scheduleIds',
      label: 'Schedules',
      type: 'multiSelect',
      tab: 'Work',
      options: schedules.map((sch) => ({ value: sch.id, label: sch.name })),
    },
    {
      value: 'appRoleIds',
      label: 'App Roles',
      type: 'multiSelect',
      tab: 'Work',
      options: roles.map((role) => ({ value: role.id, label: role.name })),
    },
    {
      value: 'payrollDepartmentId',
      label: 'Payroll Department',
      type: 'select',
      tab: 'Work',
      options: payrollDepartments.filter((dept) => dept.isActive).map((dept) => ({ value: dept.id, label: dept.name })),
    },
    {
      value: 'employmentType',
      label: 'Employment Type',
      type: 'select',
      tab: 'Work',
      options: [
        { value: 'Permanent', label: 'Permanent' },
        { value: 'Contract', label: 'Contract' },
        { value: 'Agency', label: 'Agency' },
      ],
    },
    { value: 'allowMobileClockIn', label: 'Allow Mobile Clock In', type: 'boolean', tab: 'Work' },
    {
      value: 'leavePolicyId',
      label: 'Assigned Leave Policy',
      type: 'select',
      tab: 'Leave',
      options: [
        { value: '', label: 'Any' },
        ...leavePolicies.map((lp) => ({
          value: lp.leavePolicyId,
          label: lp.isActive ? lp.policyName : `${lp.policyName} (inactive)`,
        })),
      ],
    },
    { value: 'licenseNumber', label: 'License - Number', type: 'text', tab: 'License' },
    { value: 'licenseExpiryDate', label: 'License - Expiry Date', type: 'dateRange', tab: 'License' },
    { value: 'worksNumber', label: 'Works Number', type: 'text', tab: 'Payroll' },
    { value: 'ppsNumber', label: 'PPS Number', type: 'text', tab: 'Payroll' },
    { value: 'accountNumber', label: 'Bank Account Number', type: 'text', tab: 'Payroll' },
    { value: 'accountName', label: 'Bank Account Name', type: 'text', tab: 'Payroll' },
    { value: 'bankName', label: 'Bank Name', type: 'text', tab: 'Payroll' },
    { value: 'ban', label: 'BAN', type: 'text', tab: 'Payroll' },
    { value: 'bic', label: 'BIC', type: 'text', tab: 'Payroll' },
    { value: 'sortCode', label: 'Sort Code', type: 'text', tab: 'Payroll' },
    {
      value: 'payRecordPaymentTypeId',
      label: 'Pay Type',
      type: 'select',
      tab: 'Pay Record',
      options: paymentTypes.filter((pt) => pt.isActive).map((pt) => ({ value: pt.id, label: pt.name })),
    },
    {
      value: 'payRecordPaymentFrequencyId',
      label: 'Pay Frequency',
      type: 'select',
      tab: 'Pay Record',
      options: paymentFrequencies.filter((pf) => pf.isActive).map((pf) => ({ value: pf.id, label: pf.name })),
    },
    { value: 'payRecordContractedHours', label: 'Contracted Hours (Per Week)', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordBaseRate', label: 'Pay Rate', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordSundayRateMultiplier', label: 'Sunday Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordBankHolidayRateMultiplier', label: 'Bank Holiday Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordNightShiftMultiplier', label: 'Night Shift Multiplier', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordOvertimeEligible', label: 'Is Overtime Eligible', type: 'boolean', tab: 'Pay Record' },
    { value: 'payRecordOt1TriggerHours', label: 'OT1 Trigger Hours', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordOt1RateMultiplier', label: 'OT1 Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordOt2TriggerHours', label: 'OT2 Trigger Hours', type: 'numberRange', tab: 'Pay Record' },
    { value: 'payRecordOt2RateMultiplier', label: 'OT2 Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
    { value: 'feedbackType', label: 'Feedback - Type', type: 'text', tab: 'Feedback' },
    { value: 'feedbackActionTaken', label: 'Feedback - Action Taken', type: 'text', tab: 'Feedback' },
    { value: 'feedbackDescription', label: 'Feedback - Description', type: 'text', tab: 'Feedback' },
    { value: 'feedbackPriority', label: 'Feedback - Priority', type: 'text', tab: 'Feedback' },
    { value: 'feedbackStatus', label: 'Feedback - Status', type: 'text', tab: 'Feedback' },
    { value: 'trainingUnassigned', label: 'Training - Unassigned', type: 'boolean', tab: 'Training' },
    { value: 'trainingPending', label: 'Training - Pending', type: 'boolean', tab: 'Training' },
    { value: 'trainingCompleted', label: 'Training - Completed', type: 'boolean', tab: 'Training' },
    { value: 'notesSubject', label: 'Notes - Subject', type: 'text', tab: 'Notes' },
    { value: 'notesContent', label: 'Notes - Description', type: 'text', tab: 'Notes' },
  ];
}

export interface EmployeeAdvancedFilterParams {
  allEmployees: Employee[];
  selectedLocationId: number | null | undefined;
  statusFilter: 'Active' | 'InActive' | 'All';
  filters: FilterCondition[];
  searchTerm: string;
  filterFields: FilterFieldOption[];
  sortColumn: 'name' | 'position' | 'location' | null;
  sortDirection: 'asc' | 'desc';
  allPayRecords: PayRecord[];
  allLicenses: EmployeeLicense[];
  allFeedbackRecords: FeedbackRecord[];
  allTrainingRecords: TrainingRecord[];
  allEmployeeNotes: EmployeeNote[];
}

export function filterEmployeesWithAdvancedFilters(params: EmployeeAdvancedFilterParams): Employee[] {
  const {
    allEmployees,
    selectedLocationId,
    statusFilter,
    filters,
    searchTerm,
    filterFields,
    sortColumn,
    sortDirection,
    allPayRecords,
    allLicenses,
    allFeedbackRecords,
    allTrainingRecords,
    allEmployeeNotes,
  } = params;

    let filtered = [...allEmployees];

    // STEP 1: Apply location filter from top banner FIRST (if selected)
    if (selectedLocationId != null && selectedLocationId !== 0) {
      const locId = selectedLocationId;
      filtered = filtered.filter((employee) => {
        // Check if employee has the selected location (main location OR sub location)
        const hasMainLocation = employee.profile?.mainLocationId === locId;
        const hasSubLocation = employee.profile?.subLocationIds && 
                              Array.isArray(employee.profile.subLocationIds) && 
                              employee.profile.subLocationIds.includes(locId);
        return hasMainLocation || hasSubLocation;
      });
    }

    // STEP 2: Apply status filter (Active/InActive/All)
    if (statusFilter !== 'All') {
      filtered = filtered.filter((employee) => {
        if (statusFilter === 'Active') {
          return employee.isActive === true;
        } else if (statusFilter === 'InActive') {
          return employee.isActive === false;
        }
        return true;
      });
    }

    // STEP 3: Apply advanced filters (after location and status filters)
    
    filters.forEach(filter => {
      if (!filter.field) {
        return;
      }
      // Applied earlier via statusFilter param (derived from this row on the report page).
      if (filter.field === 'employeeReportStatus') {
        return;
      }

      const fieldType = getFieldTypeFromList(filterFields, filter.field);
      
      // Skip if filter is not properly set based on field type
      if (fieldType === 'dateRange' || fieldType === 'numberRange') {
        if (!filter.valueFrom && !filter.valueTo) {
          return; // Skip if both from and to are empty
        }
      } else if (fieldType === 'multiSelect') {
        if (!Array.isArray(filter.value) || filter.value.length === 0) {
          return; // Skip if no values selected
        }
      } else {
        if (filter.value === '' || filter.value === null || filter.value === undefined) {
          return; // Skip if value is empty
        }
      }

      filtered = filtered.filter((employee) => {
        // Handle related data filters (Pay Record, License, Feedback, Training, Notes)
        if (filter.field.startsWith('payRecord')) {
          // Early return if no pay records data loaded
          if (!allPayRecords || allPayRecords.length === 0) {
            return false; // No pay records data, so no matches
          }
          
          // Get ONLY ACTIVE pay records for this employee
          // IMPORTANT: Ignore all inactive pay records when filtering
          const employeePayRecords = allPayRecords.filter(pr => {
            if (!pr) return false;
            
            // Must match employee ID (handle both number and string IDs)
            const prEmployeeId = typeof pr.employeeId === 'string' ? Number(pr.employeeId) : pr.employeeId;
            const empId = typeof employee.id === 'string' ? Number(employee.id) : employee.id;
            
            if (isNaN(prEmployeeId) || isNaN(empId) || prEmployeeId !== empId) {
              return false;
            }
            
            // STRICT CHECK: Only include records where isActive is explicitly true
            // Reject all inactive records (false, null, undefined, 0, 'false', etc.)
            if (pr.isActive !== true) {
              return false; // Ignore inactive records
            }
            
            return true;
          });
          
          console.log(`[Filter] Employee ${employee.id} (${employee.firstName} ${employee.lastName}): Found ${employeePayRecords.length} active pay records`);
          if (employeePayRecords.length === 0) {
            console.log(`[Filter] No active pay records for employee ${employee.id}, excluding`);
            return false;
          }
          
          // Log first pay record for debugging BaseRate filter
          if (employeePayRecords.length > 0 && filter.field === 'payRecordBaseRate') {
            console.log('[Filter] Sample pay record for BaseRate filter:', {
              id: employeePayRecords[0].id,
              employeeId: employeePayRecords[0].employeeId,
              baseRate: employeePayRecords[0].baseRate,
              baseRateType: typeof employeePayRecords[0].baseRate,
              isActive: employeePayRecords[0].isActive,
              availableFields: Object.keys(employeePayRecords[0])
            });
          }
          
          // Check if any pay record matches the filter
          return employeePayRecords.some(pr => {
            if (filter.field === 'payRecordPaymentTypeId') {
              // Ensure both values are numbers for comparison
              const filterValue = typeof filter.value === 'number' ? filter.value : Number(filter.value);
              if (isNaN(filterValue)) return false;
              
              const prPaymentTypeId = typeof pr.paymentTypeId === 'number' ? pr.paymentTypeId : Number(pr.paymentTypeId);
              if (isNaN(prPaymentTypeId)) return false;
              
              return prPaymentTypeId === filterValue;
            } else if (filter.field === 'payRecordPaymentFrequencyId') {
              // Ensure both values are numbers for comparison
              const filterValue = typeof filter.value === 'number' ? filter.value : Number(filter.value);
              if (isNaN(filterValue)) return false;
              
              // Handle null/undefined paymentFrequencyId - if filter is set, record must have it
              if (pr.paymentFrequencyId === null || pr.paymentFrequencyId === undefined) {
                return false;
              }
              
              const prPaymentFrequencyId = typeof pr.paymentFrequencyId === 'number' ? pr.paymentFrequencyId : Number(pr.paymentFrequencyId);
              if (isNaN(prPaymentFrequencyId)) return false;
              
              return prPaymentFrequencyId === filterValue;
            } else if (filter.field === 'payRecordContractedHours') {
              const value = pr.contractedHours;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordBaseRate') {
              const value = pr.baseRate;
              // Debug logging
              console.log('[Filter] PayRecord BaseRate filter:', {
                payRecordId: pr.id,
                employeeId: pr.employeeId,
                baseRate: value,
                baseRateType: typeof value,
                filterFrom: filter.valueFrom,
                filterTo: filter.valueTo
              });
              
              if (value === null || value === undefined) {
                console.log('[Filter] PayRecord BaseRate is null/undefined, excluding');
                return false;
              }
              
              // Handle empty strings and null/undefined - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '' && !isNaN(Number(filter.valueFrom))) 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '' && !isNaN(Number(filter.valueTo))) 
                ? Number(filter.valueTo) 
                : null;
              
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) {
                console.log('[Filter] Both from and to are null, including record');
                return true;
              }
              
              // Convert value to number for comparison
              const numValue = Number(value);
              if (isNaN(numValue)) {
                console.log('[Filter] BaseRate value is NaN, excluding');
                return false;
              }
              
              // Apply range filter
              console.log('[Filter] Comparing:', { numValue, from, to });
              if (from !== null && numValue < from) {
                console.log('[Filter] numValue < from, excluding');
                return false;
              }
              if (to !== null && numValue > to) {
                console.log('[Filter] numValue > to, excluding');
                return false;
              }
              
              console.log('[Filter] PayRecord BaseRate matches filter, including');
              return true;
            } else if (filter.field === 'payRecordSundayRateMultiplier') {
              const value = pr.sundayRateMultiplier ?? 1.0;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordBankHolidayRateMultiplier') {
              const value = pr.bankHolidayRateMultiplier ?? 1.0;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordNightShiftMultiplier') {
              const value = pr.nightShiftMultiplier ?? 1.0;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOvertimeEligible') {
              return pr.overtimeEligible === filter.value;
            } else if (filter.field === 'payRecordOt1TriggerHours') {
              const value = pr.ot1TriggerHours;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOt1RateMultiplier') {
              const value = pr.ot1RateMultiplier;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOt2TriggerHours') {
              const value = pr.ot2TriggerHours;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOt2RateMultiplier') {
              const value = pr.ot2RateMultiplier;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            }
            return false;
          });
        } else if (filter.field.startsWith('license')) {
          const employeeLicenses = allLicenses.filter(lic => lic.employeeId === employee.id);
          
          if (filter.field === 'licenseNumber') {
            if (employeeLicenses.length === 0) return false;
            const searchStr = String(filter.value || '').toLowerCase();
            return employeeLicenses.some(lic => 
              String(lic.licenseNumber || '').toLowerCase().includes(searchStr)
            );
          } else if (filter.field === 'licenseExpiryDate') {
            if (employeeLicenses.length === 0) return false;
            return employeeLicenses.some(lic => {
              if (!lic.expiryDate) return false;
              const expiryDate = new Date(lic.expiryDate).toISOString().split('T')[0];
              const from = filter.valueFrom ? String(filter.valueFrom) : null;
              const to = filter.valueTo ? String(filter.valueTo) : null;
              if (from && expiryDate < from) return false;
              if (to && expiryDate > to) return false;
              return true;
            });
          }
          return false;
        } else if (
          filter.field === 'worksNumber' ||
          filter.field === 'ppsNumber' ||
          filter.field === 'ban' ||
          filter.field === 'bic' ||
          filter.field === 'accountNumber' ||
          filter.field === 'accountName' ||
          filter.field === 'bankName' ||
          filter.field === 'sortCode'
        ) {
          const p = employee.payroll;
          if (!p) return false;
          let value: string | undefined;
          switch (filter.field) {
            case 'worksNumber':
              value = p.worksNumber;
              break;
            case 'ppsNumber':
              value = p.ppsNumber;
              break;
            case 'ban':
              value = p.ban;
              break;
            case 'bic':
              value = p.bic;
              break;
            case 'accountNumber':
              value = p.accountNumber;
              break;
            case 'accountName':
              value = p.accountName;
              break;
            case 'bankName':
              value = p.bankName;
              break;
            case 'sortCode':
              value = p.sortCode;
              break;
            default:
              return false;
          }
          if (value === undefined || value === null || String(value).trim() === '') return false;
          const searchStr = String(filter.value || '').toLowerCase();
          return String(value).toLowerCase().includes(searchStr);
        } else if (filter.field.startsWith('feedback')) {
          const employeeFeedback = allFeedbackRecords.filter(fb => fb.employeeId === employee.id);
          if (employeeFeedback.length === 0) return false;
          
          const searchStr = String(filter.value || '').toLowerCase();
          return employeeFeedback.some(fb => {
            if (filter.field === 'feedbackType') {
              return String(fb.type || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackActionTaken') {
              return String(fb.actionTaken || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackDescription') {
              return String(fb.description || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackPriority') {
              return String(fb.priority || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackStatus') {
              return String(fb.status || '').toLowerCase().includes(searchStr);
            }
            return false;
          });
        } else if (filter.field.startsWith('training')) {
          const employeeTraining = allTrainingRecords.filter(tr => tr.employeeId === employee.id);
          
          if (filter.field === 'trainingUnassigned') {
            return employeeTraining.some(tr => tr.status === 'Unassigned') === filter.value;
          } else if (filter.field === 'trainingPending') {
            return employeeTraining.some(tr => tr.status === 'Pending') === filter.value;
          } else if (filter.field === 'trainingCompleted') {
            return employeeTraining.some(tr => tr.status === 'Completed') === filter.value;
          }
          return false;
        } else if (filter.field.startsWith('notes')) {
          const employeeNotes = allEmployeeNotes.filter(note => note.employeeId === employee.id);
          if (employeeNotes.length === 0) return false;
          
          const searchStr = String(filter.value || '').toLowerCase();
          return employeeNotes.some(note => {
            if (filter.field === 'notesSubject') {
              return String(note.subject || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'notesContent') {
              return String(note.content || '').toLowerCase().includes(searchStr);
            }
            return false;
          });
        } else if (filter.field === 'mainOrSubLocationIds') {
          // Multi-select: check if employee has any of the selected locations
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const hasMainLocation = employee.profile?.mainLocationId && selectedIds.includes(employee.profile.mainLocationId);
          const hasSubLocation = employee.profile?.subLocationIds && 
            Array.isArray(employee.profile.subLocationIds) &&
            employee.profile.subLocationIds.some((id: number) => selectedIds.includes(id));
          return hasMainLocation || hasSubLocation;
        } else if (filter.field === 'mainOrSubPositionIds') {
          // Multi-select: check if employee has any of the selected positions
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const hasMainPosition = employee.profile?.mainPositionId && selectedIds.includes(employee.profile.mainPositionId);
          const subPositionIds = employee.profile?.subPositionIds;
          let hasSubPosition = false;
          if (Array.isArray(subPositionIds)) {
            hasSubPosition = subPositionIds.some((id: number) => selectedIds.includes(id));
          }
          return hasMainPosition || hasSubPosition;
        } else if (filter.field === 'scheduleIds') {
          // Multi-select: check if employee has any of the selected schedules
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const employeeSchedules = employee.profile?.scheduleIds || [];
          return Array.isArray(employeeSchedules) && 
            employeeSchedules.some((id: number) => selectedIds.includes(id));
        } else if (filter.field === 'appRoleIds') {
          // Multi-select: check if employee has any of the selected app roles
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const employeeRoleIds = employee.profile?.appRoleIds || [];
          return Array.isArray(employeeRoleIds) && 
            employeeRoleIds.some((id: number) => selectedIds.includes(id));
        } else {
          // Standard employee fields - get value from employee object or profile
          let fieldValue: any;

          // Get the actual value from employee object or profile
          if (filter.field === 'mainLocationId') {
            fieldValue = employee.profile?.mainLocationId;
          } else if (filter.field === 'mainPositionId') {
            fieldValue = employee.profile?.mainPositionId;
          } else if (filter.field === 'payrollDepartmentId') {
            fieldValue = employee.profile?.payrollDepartmentId;
          } else if (filter.field === 'employmentType') {
            fieldValue = employee.profile?.employmentType;
          } else if (filter.field === 'allowMobileClockIn') {
            fieldValue = employee.profile?.allowMobileClockIn;
          } else if (filter.field === 'leavePolicyId') {
            fieldValue = employee.leavePolicyId ?? employee.leavePolicy?.leavePolicyId ?? null;
          } else {
            // Try to get from employee object directly
            // Fields like firstName, lastName, email, phone, gender, address, etc. are on employee
            fieldValue = (employee as any)[filter.field];
            
            // If not found and field might be in profile, try profile
            if (fieldValue === undefined && employee.profile) {
              fieldValue = (employee.profile as any)[filter.field];
            }
          }

          // Handle different field types
          if (fieldType === 'boolean') {
            return fieldValue === filter.value;
          } else if (fieldType === 'dateRange') {
            if (!fieldValue) return false;
            try {
              const fieldDate = new Date(fieldValue).toISOString().split('T')[0];
              if (isNaN(new Date(fieldDate).getTime())) return false;
              const from = filter.valueFrom ? String(filter.valueFrom) : null;
              const to = filter.valueTo ? String(filter.valueTo) : null;
              if (from && fieldDate < from) return false;
              if (to && fieldDate > to) return false;
              return true;
            } catch (e) {
              return false;
            }
          } else if (fieldType === 'numberRange') {
            const numValue = Number(fieldValue);
            if (isNaN(numValue) || fieldValue === null || fieldValue === undefined) return false;
            const from = filter.valueFrom ? Number(filter.valueFrom) : null;
            const to = filter.valueTo ? Number(filter.valueTo) : null;
            if (from !== null && numValue < from) return false;
            if (to !== null && numValue > to) return false;
            return true;
          } else if (fieldType === 'select' || fieldType === 'number') {
            // Handle null/undefined - if field is null/undefined and filter has value, no match
            if ((fieldValue === null || fieldValue === undefined) && filter.value !== null && filter.value !== undefined) {
              return false;
            }
            // If filter is null/undefined, skip this filter (shouldn't happen, but be safe)
            if (filter.value === null || filter.value === undefined) {
              return true;
            }
            // For select fields, compare both as numbers if possible, otherwise as strings
            const filterVal = typeof filter.value === 'number' ? filter.value : Number(filter.value);
            const fieldVal = typeof fieldValue === 'number' ? fieldValue : Number(fieldValue);
            if (!isNaN(filterVal) && !isNaN(fieldVal)) {
              return fieldVal === filterVal;
            }
            // Fallback to string comparison
            return String(fieldValue || '') === String(filter.value || '');
          } else {
            // Text search (contains) - handle null/undefined properly
            const fieldStr = fieldValue !== null && fieldValue !== undefined 
              ? String(fieldValue).toLowerCase().trim() 
              : '';
            const filterStr = String(filter.value || '').toLowerCase().trim();
            
            // If filter is empty, skip this filter (shouldn't happen due to validation, but be safe)
            if (!filterStr) return true;
            
            // If field is empty/null and filter has value, no match
            if (!fieldStr) return false;
            
            // Perform contains search
            return fieldStr.includes(filterStr);
          }
        }
      });
    });

    // STEP 4: Apply search filter LAST (after location, status, and advanced filters)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((employee) => {
        return (
          employee.firstName?.toLowerCase().includes(searchLower) ||
          employee.lastName?.toLowerCase().includes(searchLower) ||
          employee.email?.toLowerCase().includes(searchLower) ||
          employee.address?.toLowerCase().includes(searchLower) ||
          employee.address1?.toLowerCase().includes(searchLower) ||
          employee.address2?.toLowerCase().includes(searchLower) ||
          employee.address3?.toLowerCase().includes(searchLower) ||
          employee.county?.toLowerCase().includes(searchLower) ||
          employee.zipCode?.toLowerCase().includes(searchLower)
        );
      });
    }

    // STEP 5: Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue: string = '';
        let bValue: string = '';

        if (sortColumn === 'name') {
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
        } else if (sortColumn === 'position') {
          aValue = a.profile?.mainPosition?.title || '';
          bValue = b.profile?.mainPosition?.title || '';
        } else if (sortColumn === 'location') {
          aValue = a.profile?.mainLocation?.name || a.profile?.locationName || '';
          bValue = b.profile?.mainLocation?.name || b.profile?.locationName || '';
        }

        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
}
